import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";

type Receipt = {
  hubName?: string;
  id: string;
  requestId: string;
  teamId: string;
  actorUserId?: string | null;
  actorKind?: string | null;
  source?: string | null;
  origin?: string | null;
  workspaceName?: string | null;
  actorName?: string | null;
  flowId?: string | null;
  runId?: string | null;
  model: string;
  lane: string;
  status: string;
  queuedAt: string;
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  firstTokenMs?: number | null;
  generationTokensPerSecond?: number | null;
  promptTokensPerSecond?: number | null;
  peakMemoryBytes?: number | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  contextLimitTokens?: number | null;
  inputBytes: number;
  outputBytes?: number | null;
  metering: string;
  contextReduction?: { originalBytes: number; sentBytes: number; durationMs: number } | null;
};
type Contribution = {
  policy: {
    maxConcurrentJobs: number;
    dailyComputeMinutes: number;
    maxCpuPercent: number;
    minAvailableMemoryGiB: number;
    timeZone: string;
  };
  host?: {
    at: string;
    cpuPercent?: number | null;
    availableMemoryGiB?: number | null;
    thermalState?: string | null;
  } | null;
  admission: { accepting: boolean; reason: string; offeredSlots: number; availableSlots: number };
  computeSecondsToday: number;
  active: Receipt[];
  pendingReceipts: number;
};
type NodeReport = {
  hubs: {
    id: string;
    name: string;
    status: string;
    live: boolean;
    sharingScope?: string;
    canManage?: boolean;
    lastHeartbeatAt?: string | null;
    contribution?: Contribution | null;
    advertised?: {
      provider: string;
      model: string;
      slots: number;
      capabilities?: { operations?: string[]; capabilities?: string[]; contextLength?: number; maxConcurrentJobs?: number } | null;
    }[];
  }[];
  receipts: {
    id: string;
    hubId: string;
    receipt: Receipt;
    verification: string;
    settlement: string;
  }[];
};
type Envelope<T> = { ok: boolean; data: T; message?: string };

export function NodesPanel({ pairCode }: { pairCode?: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const cache = useQueryClient();
  const [name, setName] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [sharingScope, setSharingScope] = useState("personal");
  const [contextJson, setContextJson] = useState("");
  const rpc = async <T,>(path: string, body: unknown = {}): Promise<T> => {
    const result = (await api.research.post(path, body)) as Envelope<T>;
    if (!result.ok) throw new Error(result.message ?? path);
    return result.data;
  };
  const report = useQuery({
    queryKey: ["thinkers", "nodes"],
    queryFn: () => rpc<NodeReport>("hubs.contributions", { hours: 24, limit: 100 }),
    refetchInterval: 15_000,
  });
  const create = useMutation({
    mutationFn: () => rpc<{ token: string }>("hubs.create", { name: name.trim(), sharingScope }),
    onSuccess: (data) => {
      setToken(data.token);
      setName("");
      void cache.invalidateQueries({ queryKey: ["thinkers", "nodes"] });
    },
  });
  const pairing = useQuery({
    queryKey: ["thinkers", "node-pair", pairCode],
    queryFn: () => rpc<{ name: string }>("hubs.pair.info", { code: pairCode }),
    enabled: Boolean(pairCode),
    retry: false,
  });
  const approve = useMutation({
    mutationFn: () => rpc("hubs.pair.approve", { code: pairCode, sharingScope }),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ["thinkers", "nodes"] }),
  });
  const unlink = useMutation({
    mutationFn: (id: string) => rpc("hubs.revoke", { id }),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ["thinkers", "nodes"] }),
  });
  const shrink = useMutation({
    mutationFn: () => rpc<{ blocks: { content: string }[]; reduction: { originalBytes: number; sentBytes: number; durationMs: number } }>(
      "hubs.context.shrink", { blocks: [{ id: "preview", format: "json", content: contextJson }] },
    ),
  });
  const sharing = (
    <label className="flex flex-col gap-1">
      {t("Who can use this computer?")}
      <select
        aria-label={t("Who can use this computer?")}
        className="rounded border p-2"
        value={sharingScope}
        onChange={(event) => setSharingScope(event.target.value)}
      >
        <option value="personal">{t("My account only")}</option>
        <option value="organization">{t("Everyone in the current workspace")}</option>
      </select>
    </label>
  );
  const number = (value?: number | null, suffix = "") =>
    value == null
      ? "—"
      : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
  const workloads = (rows: Receipt[]) => (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr>
            {[
              t("Origin"),
              t("Computer name"),
              t("Model"),
              t("Status"),
              t("Input tokens"),
              t("Output tokens"),
              t("Context window"),
              t("Queue wait"),
              t("Duration"),
              t("First token"),
              t("Generation speed"),
              t("Peak model memory"),
              t("Payload bytes"),
              t("Context data bytes"),
              t("Metering"),
            ].map((label) => (
              <th className="p-2" key={label}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className="border-t" key={row.id}>
              <td className="p-2">
                <div>{row.source ?? row.actorKind ?? row.lane}</div>
                <small title={row.teamId}>
                  {row.workspaceName ?? row.teamId.slice(0, 8)} · {row.actorName ?? row.lane}
                </small>
                {row.runId ? <div className="text-xs">{row.runId}</div> : null}
                {row.origin ? (
                  <div className="text-xs text-muted-foreground">{row.origin}</div>
                ) : null}
              </td>
              <td className="p-2">{row.hubName ?? "—"}</td>
              <td className="p-2">{row.model}</td>
              <td className="p-2">{row.status}</td>
              <td className="p-2">{number(row.inputTokens)}</td>
              <td className="p-2">{number(row.outputTokens)}</td>
              <td className="p-2">{number(row.contextLimitTokens)}</td>
              <td className="p-2">
                {number(
                  Math.max(0, Date.parse(row.startedAt) - Date.parse(row.queuedAt)) / 1000,
                  " s",
                )}
              </td>
              <td className="p-2">
                {number(row.durationMs == null ? null : row.durationMs / 1000, " s")}
              </td>
              <td className="p-2">{number(row.firstTokenMs, " ms")}</td>
              <td className="p-2">{number(row.generationTokensPerSecond, " tokens/s")}</td>
              <td className="p-2">
                {number(
                  row.peakMemoryBytes == null ? null : row.peakMemoryBytes / 1024 ** 3,
                  " GiB",
                )}
              </td>
              <td className="p-2">
                {number(row.inputBytes)} / {number(row.outputBytes)}
              </td>
              <td className="p-2">
                {row.contextReduction
                  ? `${number(row.contextReduction.originalBytes)} → ${number(row.contextReduction.sentBytes)}`
                  : "—"}
              </td>
              <td className="p-2">{row.metering}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      <a href="/downloads/docit/" target="_blank" rel="noopener noreferrer" className="w-fit rounded border px-3 py-2 text-sm">
        {t("Download Docit for Mac")}
      </a>
      <p>{t("Your computers contribute only within the sharing rules chosen on each device.")}</p>
      <details className="rounded-lg border p-4">
        <summary>{t("Context minifier")}</summary>
        <p className="my-2 text-sm">{t("Remove JSON formatting before using data in prompts. Values stay intact; prose and code are not summarized. No model call or credit charge.")}</p>
        <textarea
          aria-label={t("JSON context")}
          className="w-full rounded border p-2 font-mono text-sm"
          rows={5}
          value={contextJson}
          maxLength={1_000_000}
          onChange={(event) => { setContextJson(event.target.value); shrink.reset(); }}
        />
        <button type="button" className="my-2 rounded border px-3 py-2" disabled={!contextJson.trim() || shrink.isPending} onClick={() => shrink.mutate()}>
          {t("Minify JSON")}
        </button>
        {shrink.error ? <p role="alert">{shrink.error.message}</p> : null}
        {shrink.data ? (
          <div>
            <p>{t("Context data bytes")}: {number(shrink.data.reduction.originalBytes)} → {number(shrink.data.reduction.sentBytes)} · {number(shrink.data.reduction.durationMs, " ms")}</p>
            <p className="text-sm">{t("Byte reduction is measured; token savings depend on the model tokenizer.")}</p>
            <textarea aria-label={t("Minified context")} className="w-full rounded border p-2 font-mono text-sm" rows={5} readOnly value={shrink.data.blocks[0]?.content ?? ""} />
          </div>
        ) : null}
      </details>
      <p>
        {t(
          "Linked computers run your workspace upkeep instead of Docit-hosted models. Work waits while your computers are unavailable.",
        )}
      </p>
      {pairCode ? (
        <section className="rounded-lg border p-4 flex flex-col gap-3">
          <h3 className="font-semibold">{t("Link this computer to my account")}</h3>
          <code>{pairCode}</code>
          <p>{pairing.data?.name}</p>
          <p>{t("Check that this code matches the Docit app on the computer you are linking.")}</p>
          {pairing.error ? (
            <p role="alert">
              {t("This linking request expired or was already used. Start again in Docit.")}
            </p>
          ) : null}
          {approve.isSuccess ? (
            <p>{t("Computer linked. Return to Docit and enable sharing when ready.")}</p>
          ) : (
            <>
              {sharing}
              <button
                type="button"
                className="rounded border p-2"
                disabled={!pairing.data || approve.isPending}
                onClick={() => approve.mutate()}
              >
                {t("Link this computer to my account")}
              </button>
            </>
          )}
          {approve.error ? <p role="alert">{approve.error.message}</p> : null}
        </section>
      ) : null}
      {report.isLoading ? <p>{t("Loading")}</p> : null}
      {report.error ? <p role="alert">{report.error.message}</p> : null}
      {report.data?.hubs.length === 0 ? (
        <p>{t("No contributing computers connected yet.")}</p>
      ) : null}
      {report.data?.hubs.map((hub) => (
        <section className="rounded-lg border p-4" key={hub.id}>
          <h3 className="font-semibold">
            {hub.name} · {hub.live ? t("Connected") : t("Offline")}
          </h3>
          <p>
            {hub.sharingScope === "organization"
              ? t("Everyone in the current workspace")
              : hub.sharingScope === "personal"
                ? t("My account only")
                : t("Existing compute node")}
          </p>
          <details className="my-2">
            <summary>{t("Reported capabilities")}</summary>
            <p className="text-sm">{t("Device-reported metadata; available capacity follows sharing rules. Unreported capabilities remain unknown.")}</p>
            {!hub.live ? <p>{t("Last reported capabilities are stale.")}</p> : null}
            {hub.advertised?.map((advert) => (
              <div className="my-2 text-sm" key={`${advert.provider}:${advert.model}`}>
                <strong>{advert.model} · {advert.provider}</strong>
                <p>{t("Docit operations")}: {advert.capabilities?.operations?.join(", ") ?? "—"} · {t("Model slots")}: {advert.slots}</p>
                <p>{t("Context window")}: {number(advert.capabilities?.contextLength)} · {t("Service concurrency cap")}: {number(advert.capabilities?.maxConcurrentJobs)}</p>
                {advert.capabilities?.capabilities?.length ? <p>{t("Model metadata")}: {advert.capabilities.capabilities.join(", ")}</p> : null}
              </div>
            ))}
          </details>
          {hub.canManage ? (
            <button
              type="button"
              className="rounded border px-2"
              disabled={unlink.isPending}
              onClick={() => unlink.mutate(hub.id)}
            >
              {t("Unlink my computer")}
            </button>
          ) : null}
          {hub.contribution ? (
            <>
              <p>
                {hub.live
                  ? hub.contribution.admission.reason
                  : t("Last reported capacity is stale.")}
              </p>
              <p className="text-sm">
                {t("Offered slots")}: {hub.contribution.admission.offeredSlots} ·{" "}
                {t("Available now")}: {hub.live ? hub.contribution.admission.availableSlots : "—"} ·{" "}
                {t("Compute today")}: {number(hub.contribution.computeSecondsToday / 60, " min")} /{" "}
                {hub.contribution.policy.dailyComputeMinutes}
              </p>
              <p className="text-sm">
                CPU: {number(hub.contribution.host?.cpuPercent, "%")} · {t("Available memory")}:{" "}
                {number(hub.contribution.host?.availableMemoryGiB, " GiB")} ·{" "}
                {t("Pending usage records")}: {hub.contribution.pendingReceipts}
              </p>
              {hub.contribution.active.length ? (
                workloads(
                  hub.contribution.active.map((receipt) => ({ ...receipt, hubName: hub.name })),
                )
              ) : (
                <p className="text-sm">{t("No active jobs")}</p>
              )}
            </>
          ) : (
            <p>{t("Open the Docit app on this computer to configure sharing.")}</p>
          )}
        </section>
      ))}
      <h3 className="font-semibold">{t("Recent contributions")}</h3>
      <p className="text-sm">
        {t(
          "Last 100 reports in 24 hours. Device-reported usage is unpriced; unknown measurements stay empty.",
        )}
      </p>
      {workloads(
        report.data?.receipts.map((row) => ({
          ...row.receipt,
          hubName:
            report.data.hubs.find((hub) => hub.id === row.hubId)?.name ?? row.hubId.slice(0, 8),
        })) ?? [],
      )}
      {unlink.error ? <p role="alert">{unlink.error.message}</p> : null}
      {!pairCode ? (
        <section className="rounded-lg border p-4">
          <h3 className="font-semibold">{t("Connect a computer")}</h3>
          <p>
            {t(
              "In the Docit app, choose Link with my Docit account. You can also create a token here for manual setup.",
            )}
          </p>
          {sharing}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="flex gap-2"
          >
            <input
              className="rounded border p-2"
              aria-label={t("Computer name")}
              placeholder={t("Computer name")}
              value={name}
              minLength={2}
              maxLength={63}
              pattern="[a-z0-9][a-z0-9-]{1,62}"
              title="desk-macbook"
              onChange={(event) => setName(event.target.value)}
            />
            <button
              className="rounded border px-3"
              type="submit"
              disabled={!name.trim() || create.isPending}
            >
              {t("Create node token")}
            </button>
          </form>
          {create.error ? <p role="alert">{create.error.message}</p> : null}
          {token ? (
            <div className="mt-3">
              <p>{t("Shown once. Paste this token into the Docit app on that computer.")}</p>
              <code className="break-all select-all">{token}</code>
              <button
                type="button"
                className="ml-2 rounded border px-2"
                onClick={() => setToken(null)}
              >
                {t("Dismiss")}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
